import React, { useState, useEffect } from 'react';
import { api } from '../services/apiConfig';
import { anonymousQuizStorage, publicApi } from '../services/publicApi';
import PreferencesPopup from './PreferencesPopup';
import '../styles/landing.css';

const Hero = () => {
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [showVerificationForm, setShowVerificationForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isUserVerified, setIsUserVerified] = useState(false);
  // Track if user just registered (persists until page reload)
  const [isNewRegistration, setIsNewRegistration] = useState(false);
  
  // State for preferences popup
  const [showPreferencesPopup, setShowPreferencesPopup] = useState(false);

  // Check if user is already verified/logged in
  useEffect(() => {
    const userEmail = localStorage.getItem('userEmail');
    const userVerified = localStorage.getItem('userVerified');
    setIsUserVerified(userEmail && userVerified === 'true');
  }, []);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    if (!isValidEmail(email)) {
      showMessage('error', 'Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('email', email);

      // FormData: Do NOT set Content-Type manually! Axios/browser will set it correctly.
      const response = await api.post('/api/subscribe', formData);
      
      if (response.data.message) {
        showMessage('success', response.data.message);
        setShowVerificationForm(true);
      }
    } catch (error) {
      showMessage('error', error.response?.data?.message || 'An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerificationSubmit = async (e) => {
    e.preventDefault();
    
    if (!verificationCode) {
      showMessage('error', 'Please enter the verification code');
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('email', email);
      formData.append('code', verificationCode);
      formData.append('template_type', 'subscription');

      // Replace with your actual API endpoint
      const response = await api.post('/api/verify', formData);
      
      if (response.data.message && response.data.message.includes('verified')) {
        showMessage('success', response.data.message);
        
        // Reset form after successful verification and update verified status
        localStorage.setItem('userEmail', email);
        localStorage.setItem('userVerified', 'true');

        // Sync any pending anonymous quiz results
        const pendingResults = anonymousQuizStorage.get();
        if (pendingResults) {
          publicApi.syncQuizResults(email, pendingResults)
            .then(() => anonymousQuizStorage.clear())
            .catch(err => console.error('Failed to sync pending quiz results:', err));
        }
        
        // Show preferences popup instead of immediately clearing the form
        setShowPreferencesPopup(true);
      } else {
        showMessage('error', response.data.message || 'Invalid verification code');
      }
    } catch (error) {
      showMessage('error', error.response?.data?.message || 'An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => {
      setMessage({ type: '', text: '' });
    }, 5000);
  };

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Handle preferences popup success
  const handlePreferencesSuccess = (preferences) => {
    setShowPreferencesPopup(false);
    setIsUserVerified(true);
    
    // Set new registration flag - persists until page reload
    setIsNewRegistration(true);
    
    // Clear verification form
    setShowVerificationForm(false);
    setEmail('');
    setVerificationCode('');
    
    // Show success message with additional onboarding information
    showMessage('success', 'Preferences saved successfully! Your first PrepLetter is on its way. Important: To continue receiving PrepLetters, please remember to mark each one as completed after reading it. Quizzes will become available as you progress through the content.');
  };
  
  return (
    <section className="hero">
      <div className="container">
        <div className="hero-content fade-in-up">
          <div className="nps-badge">
            <span className="nps-score">9.2/10</span>
            <span className="nps-text">NPS Score from 1000+ Software Engineers</span>
          </div>
          <h1>
            Your Daily Interview Prep Buddy<br />
            <span className="highlight">1 DSA Pattern & 3 Related Problems Explained, Daily to your Email</span>
          </h1>
          <p className="hero-subtitle">
            Stay consistent, avoid burnout, and master coding interviews with daily bite-sized prep
          </p>
          
          <div className="premium-features-preview">
            <div className="feature-preview">
              <span className="feature-icon">📚</span>
              <span>100+ PrepLetters</span>
            </div>
            <div className="feature-preview">
              <span className="feature-icon">🎯</span>
              <span>Daily Quizzes</span>
            </div>
            <div className="feature-preview">
              <span className="feature-icon">📊</span>
              <span>Progress Tracking</span>
            </div>
            <div className="feature-preview">
              <span className="feature-icon">🧠</span>
              <span>Smart Accountability</span>
            </div>
          </div>

          <div className="hero-cta-row">
            <a href="/learn/1" className="hero-try-quiz-btn">
              <i className="bi bi-lightning-charge-fill"></i>
              Try a Free Quiz Now
            </a>
            <span className="hero-cta-divider">or</span>
            <a href="/learn" className="hero-browse-btn">
              Browse Free Letters
            </a>
          </div>

          {!isUserVerified && (
            <div className="email-signup">
              <h3 className="signup-title">🚀 Start Your Daily Prep Journey</h3>
              <p className="signup-subtitle">
                Get instant access to 100+ PrepLetters, daily quizzes, and smart accountability features. Just verify your email, completely FREE!
              </p>
              
              {!showVerificationForm ? (
                <form className="email-form" onSubmit={handleEmailSubmit}>
                  <input
                    type="email"
                    placeholder="Enter your email address"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <button type="submit" disabled={isLoading}>
                    {isLoading ? 'Subscribing...' : 'Subscribe'}
                  </button>
                </form>
              ) : (
                <form className="code-form" onSubmit={handleVerificationSubmit} style={{ display: 'flex' }}>
                  <input
                    type="text"
                    placeholder="Enter verification code"
                    required
                    maxLength="6"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                  />
                  <button type="submit" disabled={isLoading}>
                    {isLoading ? 'Verifying...' : 'Verify'}
                  </button>
                </form>
              )}

              {message.text && (
                <div className={`alert ${message.type}`}>{message.text}</div>
              )}

              <div className="pricing-badge">
                <span className="coffee-icon">☕</span>
                <span className="strikethrough">1 Coffee/month ($5)</span>
                <span>FREE Now!</span>
              </div>
            </div>
          )}
          
          {isUserVerified && (
            <div className="verified-user-message">
              {/* Show different message if user just completed registration vs returning */}
              {isNewRegistration ? (
                <div className="new-user-message">
                  <h3 className="signup-title">Registration Complete! 🎉</h3>
                  <p className="signup-subtitle">
                    Your first PrepLetter is on its way to your inbox. Keep in touch with us on LinkedIn: <a href="https://www.linkedin.com/company/prepletter/" target="_blank" rel="noreferrer">PrepLetter</a>
                  </p>
                </div>
              ) : (
                <div className="returning-user-message">
                  <h3 className="signup-title">Welcome Back! 🎉</h3>
                  <p className="signup-subtitle">
                    You're all set! Check out our <a href="/quizzes" className="quiz-link">practice quizzes</a> to sharpen your coding skills.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Preferences Popup */}
      <PreferencesPopup 
        isOpen={showPreferencesPopup}
        onClose={() => {
          setShowPreferencesPopup(false);
          setIsUserVerified(true);
          setShowVerificationForm(false);
          setEmail('');
          setVerificationCode('');
        }}
        email={email}
        onSuccess={handlePreferencesSuccess}
      />
    </section>
  );
};

export default Hero;
